import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons/faArrowLeft';
import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useRouteMatch } from 'react-router-dom';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import Global from '../../store/global-selectors';
import { getBatchDetails, emptyBatchDetails, getBatchDetailsOrdersShipmentsMode } from '../../store/batches/details/batch.details.actions';
import { updateOrdersPageLeftNav } from '../../store/batches/nav/batches.nav.actions';
import Header from '../../components/page-layout/header';
import ActionsSection from '../../components/page-layout/actions-section';
import OrdersBatchesGrid from './order-batches-grid';
import { OrdersShipmentsModeContainer } from '../../containers/batches/filter-bar/orders-shipments-mode.container';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import ErrorBoundary from '../../components/error-boundary';
import { OrdersGridDrawerContainer } from '../../containers/orders-drawer';
import { BatchDetailGridPagingControl } from '../../containers/batches/batch-detail-grid-paging-container';
import EditColumnsButtonContainer from '../../components/edit-columns-button/containers/orders-grid-container';
import RemoveFromBatchButtonContainer from '../../components/batches/remove-from-batch-button/container';
import TagButton from '../../components/orders-tag-button';
import ProcessShipmentsButton from '../batches/batch-detail-page/components/process-shipments-button';
import { OrdersGridPrintDocumentButton } from '../../components/orders-grid/print-document-button';
import { BatchCancelButton } from '../../components/batches/batch-cancel-button';
import { ExportOrdersButton } from '../../containers/batches/export-orders';
import { OrdersBatchesContainer as BulkUpdateButton } from '../../containers/bulk-update/batch-detail-grid-bulk-update-menu-container/orders-batches-container';
import BatchGridTitle from './orders-batches-grid-title';
import { ScanToBatchContainer } from '../../containers/batches/scan-to-batch.container';
import QuickshipGridTreatment from '../../components/grid-header/quickship-grid-treatment';
import GroupByButton from '../../components/grid-header/group-by-button';
import { useIsQuickshipAllowed } from '../../hooks/use-seller-setting';
import { useIsQuickshipEnabled } from '../../hooks/use-user-setting';
import { useEnableOrderPickerRole } from '../../hooks/use-get-order-picker-permissions';
import { QuickshipButton } from '../../components/orders-grid/quickship-button';
import { ReadyToPickButton } from './ready-to-pick/ready-to-pick-button';
import ScanOrdersButton from '../../components/batches/scan-orders-button';
import { useEnableBatchScanning } from '../../feature-flags/hooks/use-enable-batch-scanning';

import style from './orders-batches.module.less';
import gridPageStyle from '../../pages/grid-page.module.less';

export const filterBarSource = 'orders-batches-filter-bar';

const OrdersBatchesPage = ({ className, returnUrl }) => {
  const dispatch = useDispatch();

  const batch = useSelector(Global.selectBatchDetailsBatch);
  const isSidebarVisible = useSelector(Global.selectOrdersGridisSidebarVisible);
  const isLoading = useSelector(Global.selectBatchDetailsIsLoading);

  const match = useRouteMatch();
  const { url } = match;
  const { batchId } = match.params;

  const isQuickshipAllowed = useIsQuickshipAllowed();
  const shouldShowQuickshipEnabledState = useIsQuickshipEnabled();
  const isOrderPickingEnabled = useEnableOrderPickerRole();
  const isBatchScanningEnabled = useEnableBatchScanning();

  useEffect(() => {
    dispatch(updateOrdersPageLeftNav());
  }, [dispatch]);

  useEffect(() => {
    dispatch(emptyBatchDetails());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getBatchDetails(batchId));
  }, [dispatch, batchId]);

  const onGroupByChanged = useCallback(() => dispatch(getBatchDetailsOrdersShipmentsMode()), [dispatch]);

  const isInProcessed = url.includes('/shipments/batches/processed');
  const isInArchived = url.includes('/shipments/batches/archived');
  const shouldHideHeaderButtons = isInProcessed || isInArchived;

  if (!batch) {
    return <LoadingIndicator className={style['loading-indicator']} />;
  }

  return (
    <div className={classnames(className, gridPageStyle['main-content'], style['main-content'])}>
      {shouldShowQuickshipEnabledState && <QuickshipGridTreatment />}
      <Header>
        <div className={style['batch-title']}>
          {returnUrl && (
            <Link className={style['go-back-to-batches-button']} theme='unstyled' to={returnUrl}>
              <FontAwesomeIcon icon={faArrowLeft} />
            </Link>
          )}
          <BatchGridTitle batch={batch} />
        </div>
        <div className={style['right-aligned-buttons-container']}>
          <GroupByButton onGroupByChanged={onGroupByChanged} />
          <EditColumnsButtonContainer />
        </div>
      </Header>
      <ActionsSection className={style['actions-section']}>
        {!shouldHideHeaderButtons && <ProcessShipmentsButton batchId={batchId} theme='primary' />}
        {isQuickshipAllowed && <QuickshipButton pendoTrackingPrefix='shipments/batches/open' />}
        <OrdersGridPrintDocumentButton />
        <TagButton />
        {!shouldHideHeaderButtons && <BulkUpdateButton />}
        <RemoveFromBatchButtonContainer />
        {isOrderPickingEnabled && <ReadyToPickButton batchName={batch.name} fulfillmentPlanSetId={batchId} />}
        <BatchCancelButton isDetails />
        {!shouldHideHeaderButtons && <ScanToBatchContainer batchId={batchId} />}
        {!shouldHideHeaderButtons && isBatchScanningEnabled && <ScanOrdersButton />}
        <ExportOrdersButton filterSource={filterBarSource} fulfillmentPlanSetId={batchId} />
      </ActionsSection>
      <div className={style['grid-filter-wrap']}>
        <OrdersShipmentsModeContainer filterSource={filterBarSource} fulfillmentPlanSetId={batchId} />
      </div>
      <div className={style['grid-content']}>
        <div
          className={classnames(style['grid-and-footer'], {
            [style['no-sidebar']]: !isSidebarVisible,
          })}
        >
          {isLoading && <LoadingIndicator className={style['loading-indicator']} />}
          <div className={style.grid}>
            {!isLoading && (
              <ErrorBoundary errorMessageId='batchesGrid'>
                <OrdersBatchesGrid />
              </ErrorBoundary>
            )}
          </div>
          {!isLoading && (
            <div className={[style['paging-footer']]}>
              <BatchDetailGridPagingControl />
            </div>
          )}
        </div>
        {isSidebarVisible && (
          <div className={style['side-bar']}>
            <ErrorBoundary errorMessageId='drawer'>
              <OrdersGridDrawerContainer />
            </ErrorBoundary>
          </div>
        )}
      </div>
    </div>
  );
};

OrdersBatchesPage.propTypes = {
  className: PropTypes.string,
  returnUrl: PropTypes.string,
};

export default OrdersBatchesPage;
